import {
  ApplicationConfig,
  computed,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  signal,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { appRoutes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { VENDOR_CONFIG } from '@marineform/book-kit-util';
import {
  AppTranslocoLoader,
  BreakpointService,
  TimezoneInterceptor,
} from '@marineform/fe-util';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import {
  AlertsService,
  CredentialsInterceptor,
  DropdownPinPreferencesStore,
  LogoutGateInterceptor,
  ManageFileUploadAdapter,
  SessionRefreshInterceptor,
  WebsocketInterceptor,
} from '@marineform/manage-data-access';
import { MANAGE_RECORD_PAGE } from '@marineform/manage-feature';
import { provideIonicAngular } from '@ionic/angular/standalone';
import {
  provideSscFieldHorizontalFrom,
  SSC_FIELD_HORIZONTAL_FROM_TABLET,
  SSC_FIELD_LABEL_COLUMN_WIDTH,
  SSC_DROPDOWN_PIN_STORE,
  SSC_FILE_ERROR_HANDLER,
  SSC_FILE_UPLOAD_ADAPTER,
} from '@marineform/ssc-ui';
import { RecordPage } from './pages/record/record.page';
import {
  manageAppUpdateInitializer,
  manageAppearanceInitializer,
  manageAuthInitializer,
  manageFeatureFlagsInitializer,
  manageSoundEffectsInitializer,
} from './initializers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    { provide: VENDOR_CONFIG, useValue: null },
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
    { provide: MANAGE_RECORD_PAGE, useValue: RecordPage },
    {
      provide: SSC_FIELD_LABEL_COLUMN_WIDTH,
      useFactory: () => {
        const bp = inject(BreakpointService);
        return computed(() => (bp.isTablet() ? 'md' : 'sm'));
      },
    },
    provideSscFieldHorizontalFrom(() =>
      signal(SSC_FIELD_HORIZONTAL_FROM_TABLET),
    ),
    {
      provide: SSC_FILE_UPLOAD_ADAPTER,
      useExisting: ManageFileUploadAdapter,
    },
    {
      provide: SSC_DROPDOWN_PIN_STORE,
      useExisting: DropdownPinPreferencesStore,
    },
    {
      provide: SSC_FILE_ERROR_HANDLER,
      useFactory: () => {
        const alerts = inject(AlertsService);
        const transloco = inject(TranslocoService);
        return (messageKey: string) => {
          void alerts.error(transloco.translate(messageKey));
        };
      },
    },
    provideIonicAngular({
      useSetInputAPI: true,
      mode: 'ios',
      navAnimation: undefined,
    }),
    provideTransloco({
      config: {
        availableLangs: ['en', 'el'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: false,
        },
      },
      loader: AppTranslocoLoader,
    }),
    provideHttpClient(
      withInterceptors([
        LogoutGateInterceptor,
        SessionRefreshInterceptor,
        CredentialsInterceptor,
        WebsocketInterceptor,
        TimezoneInterceptor,
      ]),
    ),
    provideAppInitializer(manageFeatureFlagsInitializer),
    provideAppInitializer(manageAppearanceInitializer),
    provideAppInitializer(manageAuthInitializer),
    provideAppInitializer(manageAppUpdateInitializer),
    provideAppInitializer(manageSoundEffectsInitializer),
  ],
};
